/* import __COLOCATED_TEMPLATE__ from './sort-select.hbs'; */
/* RESPONSIBLE TEAM: team-ai-insights */

import type AppService from 'embercom/services/app-service';
import Component from '@glimmer/component';
import { inject as service } from '@ember/service';
import type IntlService from 'embercom/services/intl';
import { action } from '@ember/object';
import { taskFor } from 'ember-concurrency-ts';
import { restartableTask } from 'ember-concurrency-decorators';
import type AiContentLibraryApi from 'embercom/lib/ai-content-library/list-api';
import { type TaskGenerator } from 'ember-concurrency';
import { SortableFields, type SortDirection } from 'embercom/lib/suggestions/constants';
import { tracked } from '@glimmer/tracking';

type SortItem = {
  text: string;
  value: string;
};

type SortOption = {
  heading: string;
  items: SortItem[];
};

interface Signature {
  Args: {
    listApi: AiContentLibraryApi;
  };
}
export default class SortSelect extends Component<Signature> {
  @service declare intl: IntlService;
  @service intercomEventService: $TSFixMe;
  @service declare appService: AppService;

  @tracked selectedSortItem: SortItem | undefined;

  constructor(owner: unknown, args: Signature['Args']) {
    super(owner, args);
    this.selectedSortItem = this.sortOptions[0].items[0];
  }

  get sortOptions(): SortOption[] {
    return [
      {
        heading: this.intl.t('ai-agent.suggestions.sorting.sort-by'),
        items: [
          {
            text: this.intl.t('ai-agent.suggestions.sorting.highest-priority'),
            value: `${SortableFields.Priority}-desc`,
          },
          {
            text: this.intl.t('ai-agent.suggestions.sorting.lowest-priority'),
            value: `${SortableFields.Priority}-asc`,
          },
          {
            text: this.intl.t('ai-agent.suggestions.sorting.newest'),
            value: `${SortableFields.SuggestedAt}-desc`,
          },
          {
            text: this.intl.t('ai-agent.suggestions.sorting.oldest'),
            value: `${SortableFields.SuggestedAt}-asc`,
          },
        ],
      },
    ];
  }

  @action
  onSelectItem(value: string) {
    this.selectedSortItem = this.sortOptions[0].items.find((option) => option.value === value);
    let [sortBy, sortDirection] = value.split('-') as [SortableFields, SortDirection];
    this.intercomEventService.trackAnalyticsEvent({
      action: 'clicked',
      object: 'suggestion_sort_select',
      sort_by: sortBy,
      sort_direction: sortDirection,
    });
    taskFor(this.sortUpdate).perform(sortBy, sortDirection);
  }

  @restartableTask
  *sortUpdate(sortBy: SortableFields, sortDirection: SortDirection): TaskGenerator<void> {
    yield this.args.listApi.sortUpdate(sortBy, sortDirection);
  }
}

declare module '@glint/environment-ember-loose/registry' {
  export default interface Registry {
    'Reporting::Automation::AiInsights::OptimizeSuggestions::Suggestions::SortSelect': typeof SortSelect;
    'reporting/automation/ai-insights/optimize-suggestions/suggestions/sort-select': typeof SortSelect;
  }
}
